"use client";
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { useSignUp, useAuth, SignIn, useUser } from '@clerk/nextjs';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import axios from '@/utils/axiosInstance';
import { NP_PROGRAM_TYPES } from '@/services/student-setup-form-types';
import { ArrowLeft, Loader2, Mail, Lock, Eye, EyeOff, User, Phone, GraduationCap, MapPin, ShieldCheck, CheckCircle2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

// Constants for pending booking localStorage key
const PENDING_BOOKING_KEY = 'ace_pending_rotation_booking';
const DEFAULT_OAUTH_REDIRECT = '/student/profile';

interface StudentTrackSignupFormProps {
  track: string;
  unsafeMetadata?: Record<string, unknown>;
}

export function StudentTrackSignupForm({ track, unsafeMetadata }: StudentTrackSignupFormProps) {
  // IMG students land in their dedicated portal; NP/PA keep the existing destination.
  const defaultRedirect = track === 'img' ? '/student/img/applications' : '/student/profile';
  const { signUp, setActive, isLoaded } = useSignUp();
  const { isSignedIn, isLoaded: isAuthLoaded } = useAuth();
  const { user, isLoaded: isUserLoaded } = useUser();
  const router = useRouter();
  const searchParams = useSearchParams();
  
  // Profile completion mode (redirected from OAuth with incomplete profile)
  const isCompleteProfileMode = searchParams.get('completeProfile') === 'true';
  const returnToUrl = searchParams.get('returnTo') || DEFAULT_OAUTH_REDIRECT;
  const [profileCompletionSubmitting, setProfileCompletionSubmitting] = React.useState(false);
  const [profileCompletionError, setProfileCompletionError] = React.useState<string | null>(null);
  
  // Check if we're in OAuth continuation mode (hash contains /continue)
  const [isOAuthContinuation, setIsOAuthContinuation] = React.useState(false);
  const [pendingRedirect, setPendingRedirect] = React.useState<string | null>(null);
  
  // Capture pending redirect from localStorage on mount
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const stored = localStorage.getItem(PENDING_BOOKING_KEY);
    if (!stored) return;
    try {
      const data = JSON.parse(stored);
      if (data.returnPath || data.rotationCode) {
        const redirectUrl = data.returnPath || `/rotations/${data.rotationCode}`;
        setPendingRedirect(redirectUrl);
      }
    } catch {
      // ignore
    }
  }, []);

  // Check for OAuth continuation on mount and on hash changes
  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const updateFromHash = () => {
      const hash = window.location.hash || '';
      if (hash.includes('continue') || hash.includes('sso-callback')) {
        setIsOAuthContinuation(true);
      }
    };

    updateFromHash();
    window.addEventListener('hashchange', updateFromHash);
    return () => window.removeEventListener('hashchange', updateFromHash);
  }, []);
  
  // Redirect when user becomes signed in after OAuth continuation
  React.useEffect(() => {
    if (!isOAuthContinuation || !isAuthLoaded || !isSignedIn) return;
    const target = pendingRedirect || DEFAULT_OAUTH_REDIRECT;
    const separator = target.includes('?') ? '&' : '?';
    const finalUrl = `${target}${separator}returnedFromOAuth=true`;
    console.log('OAuth continuation complete - redirecting to:', finalUrl);
    router.push(finalUrl);
  }, [isOAuthContinuation, isAuthLoaded, isSignedIn, pendingRedirect, router]);
  const [isLoading, setIsLoading] = React.useState<'google' | 'apple' | 'email' | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [verificationStep, setVerificationStep] = React.useState(false);
  const [verificationCode, setVerificationCode] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [oauthPhone, setOauthPhone] = React.useState('');
  const [oauthCode, setOauthCode] = React.useState('');
  const [oauthNeedsVerification, setOauthNeedsVerification] = React.useState(false);
  const [oauthIsSubmitting, setOauthIsSubmitting] = React.useState(false);
  const [oauthError, setOauthError] = React.useState<string | null>(null);
  const [mounted, setMounted] = React.useState(false);
  const [captchaVisible, setCaptchaVisible] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  // Watch for CAPTCHA content
  React.useEffect(() => {
    if (!mounted) return;
    const checkCaptcha = () => {
      const el = document.getElementById('clerk-captcha');
      if (el && el.children.length > 0) {
        setCaptchaVisible(true);
      }
    };
    // Check immediately and set up observer
    checkCaptcha();
    const observer = new MutationObserver(checkCaptcha);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [mounted]);
  
  // Form fields
  const [signupDetails, setSignupDetails] = React.useState({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    phoneCountryCode: '+1',
    university: '',
    specialty: '',
    programOfStudy: '',
    zipcode: '',
  });
  
  // Prefill form from user data when in profile completion mode
  React.useEffect(() => {
    if (!isCompleteProfileMode || !isUserLoaded || !user) return;
    
    const metadata = user.unsafeMetadata as { 
      school?: string; 
      programOfStudy?: string; 
      zip?: string;
      phone?: string;
      specialty?: string;
    } | undefined;
    
    setSignupDetails(prev => ({
      ...prev,
      firstName: user.firstName || prev.firstName,
      lastName: user.lastName || prev.lastName,
      email: user.primaryEmailAddress?.emailAddress || prev.email,
      phone: metadata?.phone || user.primaryPhoneNumber?.phoneNumber || prev.phone,
      university: metadata?.school || prev.university,
      specialty: metadata?.specialty || prev.specialty,
      programOfStudy: metadata?.programOfStudy || prev.programOfStudy,
      zipcode: metadata?.zip || prev.zipcode,
    }));
  }, [isCompleteProfileMode, isUserLoaded, user]);
  
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  
  // University autocomplete
  const [universitySuggestions, setUniversitySuggestions] = React.useState<string[]>([]);
  const [isSearchingUniversities, setIsSearchingUniversities] = React.useState(false);
  const [showUniversitySuggestions, setShowUniversitySuggestions] = React.useState(false);

  // Specialty options
  const [specialties, setSpecialties] = React.useState<Array<{ id: number; name: string }>>([]);

  // Phone options
  const phoneOptions = React.useMemo(() => ([
    { code: '+1', country: 'United States', placeholder: '(555) 123-4567', maxDigits: 10 },
    { code: '+1', country: 'Canada', placeholder: '(555) 123-4567', maxDigits: 10 },
    { code: '+44', country: 'United Kingdom', placeholder: '07123 456789', maxDigits: 11 },
    { code: '+61', country: 'Australia', placeholder: '0412 345 678', maxDigits: 10 },
    { code: '+63', country: 'Philippines', placeholder: '0917 123 4567', maxDigits: 11 },
    { code: '+91', country: 'India', placeholder: '09876 54321', maxDigits: 10 },
  ]), []);

  const selectedPhoneOption = React.useMemo(() => (
    phoneOptions.find((opt) => opt.code === signupDetails.phoneCountryCode) || phoneOptions[0]
  ), [phoneOptions, signupDetails.phoneCountryCode]);

  const phonePlaceholder = selectedPhoneOption?.placeholder ?? '(555) 123-4567';

  // Fetch specialties on mount
  React.useEffect(() => {
    const fetchSpecialties = async () => {
      try {
        const response = await axios.get('/public/specialties');
        setSpecialties(response.data?.data || []);
      } catch (err) {
        console.warn('Failed to fetch specialties', err);
      }
    };
    fetchSpecialties();
  }, []);

  // University autocomplete
  React.useEffect(() => {
    const query = signupDetails.university.trim();
    if (query.length < 2) {
      setUniversitySuggestions([]);
      return;
    }

    const timeout = setTimeout(async () => {
      setIsSearchingUniversities(true);
      try {
        const response = await axios.get('/universities', {
          params: { q: query, limit: 6 },
        });
        const data = response.data?.data || [];
        const names = data
          .map((item: { name?: string }) => item?.name)
          .filter((name: string | undefined): name is string => Boolean(name));
        setUniversitySuggestions(names);
      } catch (err) {
        console.warn('Failed to fetch university suggestions', err);
        setUniversitySuggestions([]);
      } finally {
        setIsSearchingUniversities(false);
      }
    }, 250);

    return () => clearTimeout(timeout);
  }, [signupDetails.university]);

  const sanitizePhoneToE164 = React.useCallback(() => {
    const raw = signupDetails.phone || '';
    const digitsOnly = raw.replace(/\D/g, '');
    if (raw.trim().startsWith('+')) {
      return `+${digitsOnly}`;
    }
    if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
      return `+${digitsOnly}`;
    }
    return `${signupDetails.phoneCountryCode}${digitsOnly}`;
  }, [signupDetails.phone, signupDetails.phoneCountryCode]);

  const sanitizeOauthPhoneToE164 = React.useCallback(() => {
    const raw = oauthPhone || '';
    const digitsOnly = raw.replace(/\D/g, '');
    if (raw.trim().startsWith('+')) {
      return `+${digitsOnly}`;
    }
    return `${signupDetails.phoneCountryCode}${digitsOnly}`;
  }, [oauthPhone, signupDetails.phoneCountryCode]);

  const handleOauthPhoneSubmit = async () => {
    if (!isLoaded || !signUp) {
      setOauthError('Sign-up is still loading. Please try again.');
      return;
    }

    if (!oauthPhone.trim()) {
      setOauthError('Enter your phone number to continue.');
      return;
    }

    setOauthIsSubmitting(true);
    setOauthError(null);

    try {
      const phoneE164 = sanitizeOauthPhoneToE164();
      await signUp.update({ phoneNumber: phoneE164 });
      await signUp.prepareVerification({ strategy: 'phone_code' });
      setOauthNeedsVerification(true);
      setOauthCode('');
    } catch (err: unknown) {
      const clerkErrors = (err as { errors?: Array<{ message?: string }> })?.errors;
      if (clerkErrors && clerkErrors.length > 0) {
        setOauthError(clerkErrors.map((e) => e.message).filter(Boolean).join(' '));
      } else {
        setOauthError('Failed to save phone number. Please try again.');
      }
    } finally {
      setOauthIsSubmitting(false);
    }
  };

  const handleOauthVerify = async () => {
    if (!isLoaded || !signUp) {
      setOauthError('Sign-up is still loading. Please try again.');
      return;
    }

    if (!oauthCode.trim()) {
      setOauthError('Enter the verification code.');
      return;
    }

    setOauthIsSubmitting(true);
    setOauthError(null);

    try {
      const attempt = await signUp.attemptVerification({
        strategy: 'phone_code',
        code: oauthCode.trim(),
      });

      if (attempt.status === 'complete' && attempt.createdSessionId) {
        await setActive({ session: attempt.createdSessionId });
      } else {
        setOauthError('Verification incomplete. Please try again.');
      }
    } catch (err: unknown) {
      const clerkErrors = (err as { errors?: Array<{ message?: string }> })?.errors;
      if (clerkErrors && clerkErrors.length > 0) {
        setOauthError(clerkErrors.map((e) => e.message).filter(Boolean).join(' '));
      } else {
        setOauthError('Verification failed. Please try again.');
      }
    } finally {
      setOauthIsSubmitting(false);
    }
  };

  const handleOAuth = async (provider: 'google' | 'apple') => {
    if (!isLoaded || !signUp) {
      setError('Sign-up is still loading. Please try again.');
      return;
    }

    setIsLoading(provider);
    setError(null);

    try {
      const strategy = provider === 'google' ? 'oauth_google' : 'oauth_apple';
      
      // Create the sign-up to get the redirect URL
      const result = await signUp.create({
        strategy,
        redirectUrl: `${window.location.origin}/sso-callback`,
        actionCompleteRedirectUrl: `${window.location.origin}${defaultRedirect}`,
        unsafeMetadata,
      });

      const externalUrl = result.verifications?.externalAccount?.externalVerificationRedirectURL;
      if (externalUrl) {
        window.location.href = externalUrl.toString();
      } else {
        setError('Could not start sign-up. Please try again.');
        setIsLoading(null);
      }
    } catch (err: unknown) {
      const clerkErrors = (err as { errors?: Array<{ message?: string }> })?.errors;
      if (clerkErrors && clerkErrors.length > 0) {
        setError(clerkErrors.map((e) => e.message).filter(Boolean).join(' '));
      } else {
        setError(`Failed to sign up with ${provider === 'google' ? 'Google' : 'Apple'}. Please try again.`);
      }
      setIsLoading(null);
    }
  };

  const canSubmit = 
    signupDetails.email &&
    signupDetails.firstName &&
    signupDetails.lastName &&
    signupDetails.university &&
    signupDetails.programOfStudy &&
    signupDetails.zipcode &&
    password &&
    confirmPassword;

  const handleEmailSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !signUp) {
      setError('Sign-up is still loading. Please try again.');
      return;
    }

    if (!canSubmit) {
      setError('Please fill in all required fields.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsLoading('email');
    setError(null);

    try {
      await signUp.create({
        emailAddress: signupDetails.email,
        password,
        firstName: signupDetails.firstName,
        lastName: signupDetails.lastName,
        unsafeMetadata: {
          ...unsafeMetadata,
          phone: sanitizePhoneToE164() || undefined,
          school: signupDetails.university,
          specialty: signupDetails.specialty || undefined,
          programOfStudy: signupDetails.programOfStudy,
          zip: signupDetails.zipcode,
        },
      });

      // Send email verification
      await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
      setVerificationStep(true);
    } catch (err: unknown) {
      const clerkErrors = (err as { errors?: Array<{ message?: string; code?: string }> })?.errors;
      if (clerkErrors && clerkErrors.length > 0) {
        setError(clerkErrors.map((e) => e.message).filter(Boolean).join(' '));
      } else {
        setError('Sign-up failed. Please try again.');
      }
    } finally {
      setIsLoading(null);
    }
  };

  const handleVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !signUp) {
      setError('Sign-up is still loading. Please try again.');
      return;
    }

    if (!verificationCode) {
      setError('Please enter the verification code.');
      return;
    }

    setIsLoading('email');
    setError(null);

    try {
      const result = await signUp.attemptEmailAddressVerification({
        code: verificationCode,
      });

      if (result.status === 'complete' && result.createdSessionId) {
        await setActive({ session: result.createdSessionId });
        window.location.href = defaultRedirect;
      } else {
        setError('Verification incomplete. Please try again.');
      }
    } catch (err: unknown) {
      const clerkErrors = (err as { errors?: Array<{ message?: string }> })?.errors;
      if (clerkErrors && clerkErrors.length > 0) {
        setError(clerkErrors.map((e) => e.message).filter(Boolean).join(' '));
      } else {
        setError('Verification failed. Please try again.');
      }
    } finally {
      setIsLoading(null);
    }
  };

  const trackCopy: Record<string, { heading: string; subtitle: string }> = {
    np: {
      heading: 'Nurse Practitioner',
      subtitle: 'Create your student account',
    },
    ap: {
      heading: 'Physician Assistant',
      subtitle: 'Create your student account',
    },
    img: {
      heading: 'IMG Track',
      subtitle: 'Create your student account',
    },
  };
  const c = trackCopy[track] || trackCopy.np;

  // Check which profile fields are missing
  const profileMissingFields = React.useMemo(() => {
    if (!isCompleteProfileMode || !user) return { firstName: true, lastName: true, phone: true, university: true, programOfStudy: true, zipcode: true };
    
    const metadata = user.unsafeMetadata as { 
      school?: string; 
      programOfStudy?: string; 
      zip?: string;
      phone?: string;
    } | undefined;
    
    return {
      firstName: !user.firstName || user.firstName === 'First Name',
      lastName: !user.lastName || user.lastName === 'Last Name',
      phone: !metadata?.phone && !user.primaryPhoneNumber?.phoneNumber,
      university: !metadata?.school,
      programOfStudy: !metadata?.programOfStudy,
      zipcode: !metadata?.zip,
    };
  }, [isCompleteProfileMode, user]);
  
  const canSubmitProfileCompletion = 
    signupDetails.firstName &&
    signupDetails.lastName &&
    signupDetails.university &&
    signupDetails.programOfStudy &&
    signupDetails.zipcode;

  // Handle profile completion form submission
  const handleProfileCompletion = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user || !canSubmitProfileCompletion) {
      setProfileCompletionError('Please fill in all required fields.');
      return;
    }
    
    setProfileCompletionSubmitting(true);
    setProfileCompletionError(null);
    
    try {
      // Update user's name if changed
      const updates: { firstName?: string; lastName?: string } = {};
      if (signupDetails.firstName && signupDetails.firstName !== user.firstName) {
        updates.firstName = signupDetails.firstName;
      }
      if (signupDetails.lastName && signupDetails.lastName !== user.lastName) {
        updates.lastName = signupDetails.lastName;
      }
      
      if (Object.keys(updates).length > 0) {
        await user.update(updates);
      }
      
      // Update unsafeMetadata with profile data
      await user.update({
        unsafeMetadata: {
          ...user.unsafeMetadata,
          role: 'student',
          type: track.toUpperCase(),
          phone: sanitizePhoneToE164() || undefined,
          school: signupDetails.university,
          specialty: signupDetails.specialty || undefined,
          programOfStudy: signupDetails.programOfStudy,
          zip: signupDetails.zipcode,
        },
      });
      
      // Redirect to original destination
      const decodedReturnTo = decodeURIComponent(returnToUrl);
      console.log('Profile completion - redirecting to:', decodedReturnTo);
      window.location.href = decodedReturnTo;
      
    } catch (err: unknown) {
      console.error('Profile completion error:', err);
      const clerkErrors = (err as { errors?: Array<{ message?: string }> })?.errors;
      if (clerkErrors && clerkErrors.length > 0) {
        setProfileCompletionError(clerkErrors.map((e) => e.message).filter(Boolean).join(' '));
      } else {
        setProfileCompletionError('Failed to save profile. Please try again.');
      }
    } finally {
      setProfileCompletionSubmitting(false);
    }
  };

  if (verificationStep) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        {/* Back button */}
        <div className={`p-6 transition-all duration-500 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>
          <button
            onClick={() => setVerificationStep(false)}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors group"
          >
            <ArrowLeft className="h-5 w-5 transition-transform group-hover:-translate-x-1" />
            <span className="text-sm font-medium">Back to sign up</span>
          </button>
        </div>

        {/* Form Container */}
        <div className="flex-1 flex items-center justify-center px-6 pb-12">
          <div className={`w-full max-w-md transition-all duration-700 delay-100 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            {/* Logo */}
            <div className="text-center mb-8">
              <Link href="/">
                <Image 
                  src="/images/Ace-Main-Logo.png" 
                  alt="ACE Logo" 
                  width={200} 
                  height={80}
                  className="h-20 w-auto object-contain mx-auto"
                  priority
                />
              </Link>
            </div>

            {/* Verification Card */}
            <div className="bg-white rounded-2xl shadow-xl shadow-gray-200/50 border border-gray-100 p-8">
              <div className="text-center mb-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 rounded-full text-sm font-medium mb-4">
                  <Mail className="w-4 h-4" />
                  Check your email
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-1">Verify your email</h2>
                <p className="text-gray-500 text-sm">We sent a code to {signupDetails.email}</p>
              </div>

              {error && (
                <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 animate-in slide-in-from-top-2 duration-300">
                  {error}
                </div>
              )}

              <form onSubmit={handleVerification} className="space-y-4">
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  placeholder="Enter verification code"
                  className="w-full h-12 rounded-xl border border-gray-200 bg-gray-50 px-4 text-sm text-center tracking-widest placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all duration-200"
                  disabled={isLoading !== null}
                  autoFocus
                />
                <button
                  type="submit"
                  disabled={isLoading !== null || !verificationCode}
                  className="w-full h-12 rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 text-white text-sm font-semibold flex items-center justify-center gap-2 hover:from-blue-500 hover:to-blue-600 hover:shadow-lg hover:shadow-blue-500/25 active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none"
                >
                  {isLoading === 'email' ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Verify email'
                  )}
                </button>
              </form>
            </div>

            {/* Footer */}
            <p className="mt-8 text-center text-xs text-gray-400">
              © 2024 American Clinical Experience
            </p>
          </div>
        </div>
      </div>
    );
  }

  // If we're in OAuth continuation mode, handle missing requirements or render Clerk SignIn
  if (isOAuthContinuation) {
    // Determine redirect URL based on pending booking or default
    const baseRedirect = pendingRedirect || DEFAULT_OAUTH_REDIRECT;
    const afterSignUpUrl = `${baseRedirect}${baseRedirect.includes('?') ? '&' : '?'}returnedFromOAuth=true`;
    const missingPhone = Boolean(signUp?.missingFields?.includes('phone_number'));
    
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        {/* Form Container */}
        <div className="flex-1 flex items-center justify-center px-6 py-12">
          <div className={`w-full max-w-md transition-all duration-700 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            {/* Logo */}
            <div className="text-center mb-8">
              <Link href="/">
                <Image 
                  src="/images/Ace-Main-Logo.png" 
                  alt="ACE Logo" 
                  width={200} 
                  height={80}
                  className="h-20 w-auto object-contain mx-auto"
                  priority
                />
              </Link>
            </div>

            {/* Card */}
            <div className="bg-white rounded-2xl shadow-xl shadow-gray-200/50 border border-gray-100 p-8">
              <div className="text-center mb-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-full text-sm font-medium mb-4">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Setting up account
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-1">Completing sign-in...</h2>
                <p className="text-gray-500 text-sm">Please wait while we set up your account</p>
                {pendingRedirect && (
                  <p className="text-xs text-blue-600 mt-2">You'll be redirected to your rotation page</p>
                )}
              </div>
              
              {oauthError && (
                <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 animate-in slide-in-from-top-2 duration-300">
                  {oauthError}
                </div>
              )}

              {missingPhone ? (
                <div className="space-y-4">
                  <div className="text-sm text-gray-600">
                    Your account needs a phone number to finish sign-up.
                  </div>
                  <div className="flex gap-2">
                    <select
                      value={signupDetails.phoneCountryCode}
                      onChange={(e) =>
                        setSignupDetails((prev) => ({
                          ...prev,
                          phoneCountryCode: e.target.value,
                        }))
                      }
                      className="h-12 rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all duration-200"
                    >
                      {phoneOptions.map((opt) => (
                        <option key={`${opt.country}-${opt.code}`} value={opt.code}>
                          {opt.code}
                        </option>
                      ))}
                    </select>
                    <input
                      value={oauthPhone}
                      onChange={(e) => setOauthPhone(e.target.value)}
                      type="tel"
                      inputMode="tel"
                      className="flex-1 h-12 rounded-xl border border-gray-200 bg-gray-50 px-4 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all duration-200"
                      placeholder={phonePlaceholder}
                    />
                  </div>

                  {!oauthNeedsVerification ? (
                    <button
                      type="button"
                      onClick={handleOauthPhoneSubmit}
                      disabled={oauthIsSubmitting}
                      className="w-full h-12 rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 text-white text-sm font-semibold flex items-center justify-center gap-2 hover:from-blue-500 hover:to-blue-600 hover:shadow-lg hover:shadow-blue-500/25 active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {oauthIsSubmitting ? (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin" />
                          Sending code...
                        </>
                      ) : (
                        'Send verification code'
                      )}
                    </button>
                  ) : (
                    <>
                      <input
                        value={oauthCode}
                        onChange={(e) => setOauthCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        className="w-full h-12 rounded-xl border border-gray-200 bg-gray-50 px-4 text-sm text-center tracking-widest placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all duration-200"
                        placeholder="Enter verification code"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={6}
                      />
                      <button
                        type="button"
                        onClick={handleOauthVerify}
                        disabled={oauthIsSubmitting}
                        className="w-full h-12 rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 text-white text-sm font-semibold flex items-center justify-center gap-2 hover:from-blue-500 hover:to-blue-600 hover:shadow-lg hover:shadow-blue-500/25 active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {oauthIsSubmitting ? (
                          <>
                            <Loader2 className="h-5 w-5 animate-spin" />
                            Verifying...
                          </>
                        ) : (
                          'Verify & Continue'
                        )}
                      </button>
                    </>
                  )}
                </div>
              ) : (
                <SignIn
                  routing="hash"
                  afterSignUpUrl={afterSignUpUrl}
                  afterSignInUrl={afterSignUpUrl}
                  appearance={{
                    elements: {
                      rootBox: 'w-full',
                      card: 'shadow-none border-0 bg-transparent',
                    },
                  }}
                />
              )}
            </div>

            {/* Footer */}
            <p className="mt-8 text-center text-xs text-gray-400">
              © 2024 American Clinical Experience
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Profile completion mode - OAuth user needs to fill in missing profile data
  if (isCompleteProfileMode && isUserLoaded && user) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        {/* Form Container */}
        <div className="flex-1 flex items-center justify-center px-6 py-12">
          <div className={`w-full max-w-md transition-all duration-700 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            {/* Logo */}
            <div className="text-center mb-8">
              <Link href="/">
                <Image 
                  src="/images/Ace-Main-Logo.png" 
                  alt="ACE Logo" 
                  width={200} 
                  height={80}
                  className="h-20 w-auto object-contain mx-auto"
                  priority
                />
              </Link>
            </div>

            {/* Profile Completion Card */}
            <div className="bg-white rounded-2xl shadow-xl shadow-gray-200/50 border border-gray-100 p-8">
              <div className="text-center mb-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 rounded-full text-sm font-medium mb-4">
                  <CheckCircle2 className="w-4 h-4" />
                  Almost there!
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-1">Complete Your Profile</h2>
                <p className="text-gray-500 text-sm">We need a few more details to set up your account</p>
              </div>

              {profileCompletionError && (
                <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 animate-in slide-in-from-top-2 duration-300">
                  {profileCompletionError}
                </div>
              )}

              <form onSubmit={handleProfileCompletion} className="space-y-4">
                {/* Name Fields - show if missing */}
                {(profileMissingFields.firstName || profileMissingFields.lastName) && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="relative group">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 transition-colors group-focus-within:text-blue-500" />
                      <input
                        type="text"
                        value={signupDetails.firstName}
                        onChange={(e) => setSignupDetails((prev) => ({ ...prev, firstName: e.target.value }))}
                        placeholder="First name *"
                        className="w-full h-12 pl-12 pr-4 rounded-xl border border-gray-200 bg-gray-50 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all duration-200"
                        disabled={profileCompletionSubmitting}
                      />
                    </div>
                    <input
                      type="text"
                      value={signupDetails.lastName}
                      onChange={(e) => setSignupDetails((prev) => ({ ...prev, lastName: e.target.value }))}
                      placeholder="Last name *"
                      className="w-full h-12 px-4 rounded-xl border border-gray-200 bg-gray-50 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all duration-200"
                      disabled={profileCompletionSubmitting}
                    />
                  </div>
                )}

                {/* Phone - show if missing */}
                {profileMissingFields.phone && (
                  <div className="flex gap-2">
                    <select
                      value={signupDetails.phoneCountryCode}
                      onChange={(e) => setSignupDetails((prev) => ({ ...prev, phoneCountryCode: e.target.value }))}
                      className="h-12 rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all duration-200"
                      disabled={profileCompletionSubmitting}
                    >
                      {phoneOptions.map((opt) => (
                        <option key={`${opt.country}-${opt.code}`} value={opt.code}>
                          {opt.code}
                        </option>
                      ))}
                    </select>
                    <div className="relative group flex-1">
                      <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 transition-colors group-focus-within:text-blue-500" />
                      <input
                        type="tel"
                        inputMode="tel"
                        value={signupDetails.phone}
                        onChange={(e) => {
                          const cleaned = e.target.value.replace(/[^\d\s\-\(\)]/g, '');
                          const digitsOnly = cleaned.replace(/\D/g, '');
                          if (selectedPhoneOption?.maxDigits && digitsOnly.length > selectedPhoneOption.maxDigits) {
                            return;
                          }
                          setSignupDetails((prev) => ({ ...prev, phone: cleaned }));
                        }}
                        placeholder={phonePlaceholder}
                        className="w-full h-12 pl-12 pr-4 rounded-xl border border-gray-200 bg-gray-50 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all duration-200"
                        disabled={profileCompletionSubmitting}
                      />
                    </div>
                  </div>
                )}

                {/* University - show if missing */}
                {profileMissingFields.university && (
                  <div className="relative">
                    <div className="relative group">
                      <GraduationCap className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 transition-colors group-focus-within:text-blue-500" />
                      <input
                        type="text"
                        value={signupDetails.university}
                        onChange={(e) => setSignupDetails((prev) => ({ ...prev, university: e.target.value }))}
                        onFocus={() => setShowUniversitySuggestions(true)}
                        onBlur={() => setTimeout(() => setShowUniversitySuggestions(false), 150)}
                        placeholder="School / University *"
                        className="w-full h-12 pl-12 pr-4 rounded-xl border border-gray-200 bg-gray-50 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all duration-200"
                        disabled={profileCompletionSubmitting}
                      />
                    </div>
                    {showUniversitySuggestions && (universitySuggestions.length > 0 || isSearchingUniversities) && (
                      <div className="absolute z-10 mt-1 w-full rounded-xl border border-gray-200 bg-white shadow-lg max-h-48 overflow-auto">
                        <ul className="py-1">
                          {isSearchingUniversities && (
                            <li className="px-4 py-2 text-xs text-gray-500 flex items-center gap-2">
                              <Loader2 className="h-3 w-3 animate-spin" />
                              Loading suggestions...
                            </li>
                          )}
                          {universitySuggestions.map((name) => (
                            <li key={name}>
                              <button
                                type="button"
                                onClick={() => {
                                  setSignupDetails((prev) => ({ ...prev, university: name }));
                                  setShowUniversitySuggestions(false);
                                }}
                                className="w-full text-left px-4 py-2 text-sm hover:bg-blue-50 transition-colors"
                              >
                                {name}
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {/* Specialty - always show as optional */}
                <Select
                  value={signupDetails.specialty}
                  onValueChange={(value) => setSignupDetails((prev) => ({ ...prev, specialty: value }))}
                  disabled={profileCompletionSubmitting}
                >
                  <SelectTrigger className="w-full h-12 px-4 rounded-xl border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all duration-200">
                    <SelectValue placeholder="Specialty (optional)" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60 overflow-y-auto">
                    {specialties.map((spec) => (
                      <SelectItem key={spec.id} value={spec.name}>
                        {spec.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Program of Study - show if missing */}
                {profileMissingFields.programOfStudy && (
                  <Select
                    value={signupDetails.programOfStudy}
                    onValueChange={(value) => setSignupDetails((prev) => ({ ...prev, programOfStudy: value }))}
                    disabled={profileCompletionSubmitting}
                  >
                    <SelectTrigger className="w-full h-12 px-4 rounded-xl border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all duration-200">
                      <SelectValue placeholder="Program of Study *" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60 overflow-y-auto">
                      {NP_PROGRAM_TYPES.map((program) => (
                        <SelectItem key={program.value} value={program.value}>
                          {program.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                {/* Zip Code - show if missing */}
                {profileMissingFields.zipcode && (
                  <div className="relative group">
                    <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 transition-colors group-focus-within:text-blue-500" />
                    <input
                      type="text"
                      value={signupDetails.zipcode}
                      onChange={(e) => setSignupDetails((prev) => ({ ...prev, zipcode: e.target.value }))}
                      placeholder="Zip Code *"
                      className="w-full h-12 pl-12 pr-4 rounded-xl border border-gray-200 bg-gray-50 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all duration-200"
                      disabled={profileCompletionSubmitting}
                    />
                  </div>
                )}

                <button
                  type="submit"
                  disabled={profileCompletionSubmitting || !canSubmitProfileCompletion}
                  className="w-full h-12 rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 text-white text-sm font-semibold flex items-center justify-center gap-2 hover:from-blue-500 hover:to-blue-600 hover:shadow-lg hover:shadow-blue-500/25 active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none"
                >
                  {profileCompletionSubmitting ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Continue'
                  )}
                </button>
              </form>
            </div>

            {/* Footer */}
            <p className="mt-8 text-center text-xs text-gray-400">
              © 2024 American Clinical Experience
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Back button */}
      <div className={`p-6 transition-all duration-500 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>
        <button
          onClick={() => router.push('/')}
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors group"
        >
          <ArrowLeft className="h-5 w-5 transition-transform group-hover:-translate-x-1" />
          <span className="text-sm font-medium">Back to home</span>
        </button>
      </div>

      {/* Form Container */}
      <div className="flex-1 flex items-center justify-center px-6 pb-12">
        <div className={`w-full max-w-md transition-all duration-700 delay-100 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {/* Logo */}
          <div className="text-center mb-8">
            <Link href="/">
              <Image 
                src="/images/Ace-Main-Logo.png" 
                alt="ACE Logo" 
                width={200} 
                height={80}
                className="h-20 w-auto object-contain mx-auto"
                priority
              />
            </Link>
          </div>

          {/* Signup Card */}
          <div className="bg-white rounded-2xl shadow-xl shadow-gray-200/50 border border-gray-100 p-8">
            {/* Header */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-full text-sm font-medium mb-4">
                <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                Student Portal
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-1">{c.heading}</h2>
              <p className="text-gray-500 text-sm">{c.subtitle}</p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 animate-in slide-in-from-top-2 duration-300">
                {error}
              </div>
            )}

            {/* OAuth Buttons */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              <button
                type="button"
                onClick={() => handleOAuth('google')}
                disabled={isLoading !== null}
                className="h-12 rounded-xl border border-gray-200 bg-white text-gray-700 text-sm font-medium flex items-center justify-center gap-2 hover:bg-gray-50 hover:border-gray-300 hover:shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading === 'google' ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 16 16" fill="none">
                      <path fill="#4285F4" d="M14.9 8.161c0-.476-.039-.954-.121-1.422h-6.64v2.695h3.802a3.24 3.24 0 01-1.407 2.127v1.75h2.269c1.332-1.22 2.097-3.02 2.097-5.15z" />
                      <path fill="#34A853" d="M8.14 15c1.898 0 3.499-.62 4.665-1.69l-2.268-1.749c-.631.427-1.446.669-2.395.669-1.836 0-3.393-1.232-3.952-2.888H1.85v1.803A7.044 7.044 0 008.14 15z" />
                      <path fill="#FBBC04" d="M4.187 9.342a4.17 4.17 0 010-2.68V4.859H1.849a6.97 6.97 0 000 6.286l2.338-1.803z" />
                      <path fill="#EA4335" d="M8.14 3.77a3.837 3.837 0 012.7 1.05l2.01-1.999a6.786 6.786 0 00-4.71-1.82 7.042 7.042 0 00-6.29 3.858L4.186 6.66c.556-1.658 2.116-2.89 3.952-2.89z" />
                    </svg>
                    Google
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => handleOAuth('apple')}
                disabled={isLoading !== null}
                className="h-12 rounded-xl bg-black text-white text-sm font-medium flex items-center justify-center gap-2 hover:bg-gray-800 hover:shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading === 'apple' ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M16.365 1.43c0 1.14-.42 2.07-1.252 2.893-.83.814-1.767 1.29-2.807 1.218-.055-1.09.38-2.08 1.19-2.917.81-.84 1.856-1.32 2.87-1.38.002.062.002.124.002.186zM21.596 17.44c-.62 1.44-.99 2.18-1.85 3.52-1.2 1.86-2.89 4.17-5.01 4.19-1.17.012-1.47-.76-3.06-.76-1.59 0-1.93.74-3.14.772-2.07.06-3.65-2.12-4.85-3.97-3.34-5.11-3.7-11.1-1.64-14.29 1.46-2.26 3.77-3.58 5.94-3.58 2.22 0 3.62.76 5.45.76 1.78 0 2.89-.76 5.06-.76.88 0 3.22.32 4.73 2.42-4.07 2.22-3.42 7.99.06 10.45-.84 1.64-1.83 2.76-1.66 2.99z" />
                    </svg>
                    Apple
                  </>
                )}
              </button>
            </div>

            {/* CAPTCHA container - always in DOM */}
            <div id="clerk-captcha" className={captchaVisible ? "hidden" : "flex justify-center"} />
            
            {/* CAPTCHA modal - rendered via portal when visible */}
            {mounted && captchaVisible && ReactDOM.createPortal(
              <div className="fixed inset-0 z-[9999]">
                <div className="absolute inset-0 bg-black/50" />
                <div className="absolute inset-0 flex items-center justify-center p-4">
                  <div className="relative bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
                    <div className="flex flex-col items-center text-center mb-6">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center mb-4">
                        <ShieldCheck className="w-8 h-8 text-blue-600" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">Security Check</h3>
                      <p className="text-sm text-gray-500">Please complete the verification below</p>
                    </div>
                    <div className="flex justify-center" ref={(el) => {
                      if (el) {
                        const captcha = document.getElementById('clerk-captcha');
                        if (captcha && captcha.children.length > 0) {
                          el.innerHTML = '';
                          while (captcha.firstChild) {
                            el.appendChild(captcha.firstChild);
                          }
                        }
                      }
                    }} />
                  </div>
                </div>
              </div>,
              document.body
            )}

            {/* Divider */}
            <div className="flex items-center gap-4 mb-6">
              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
              <span className="text-xs text-gray-400 font-medium uppercase tracking-wide">or use email</span>
              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
            </div>

            {/* Email Form */}
            <form onSubmit={handleEmailSignUp} className="space-y-4">
              {/* Email */}
              <div className="relative group">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 transition-colors group-focus-within:text-blue-500" />
                <input
                  type="email"
                  value={signupDetails.email}
                  onChange={(e) => setSignupDetails((prev) => ({ ...prev, email: e.target.value }))}
                  placeholder="Email address *"
                  className="w-full h-12 pl-12 pr-4 rounded-xl border border-gray-200 bg-gray-50 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all duration-200"
                  disabled={isLoading !== null}
                />
              </div>

              {/* Name Fields */}
              <div className="grid grid-cols-2 gap-3">
                <div className="relative group">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 transition-colors group-focus-within:text-blue-500" />
                  <input
                    type="text"
                    value={signupDetails.firstName}
                    onChange={(e) => setSignupDetails((prev) => ({ ...prev, firstName: e.target.value }))}
                    placeholder="First name *"
                    className="w-full h-12 pl-12 pr-4 rounded-xl border border-gray-200 bg-gray-50 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all duration-200"
                    disabled={isLoading !== null}
                  />
                </div>
                <input
                  type="text"
                  value={signupDetails.lastName}
                  onChange={(e) => setSignupDetails((prev) => ({ ...prev, lastName: e.target.value }))}
                  placeholder="Last name *"
                  className="w-full h-12 px-4 rounded-xl border border-gray-200 bg-gray-50 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all duration-200"
                  disabled={isLoading !== null}
                />
              </div>

              {/* Phone */}
              <div className="flex gap-2">
                <select
                  value={signupDetails.phoneCountryCode}
                  onChange={(e) => setSignupDetails((prev) => ({ ...prev, phoneCountryCode: e.target.value }))}
                  className="h-12 rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all duration-200"
                  disabled={isLoading !== null}
                >
                  {phoneOptions.map((opt) => (
                    <option key={`${opt.country}-${opt.code}`} value={opt.code}>
                      {opt.code}
                    </option>
                  ))}
                </select>
                <div className="relative group flex-1">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 transition-colors group-focus-within:text-blue-500" />
                  <input
                    type="tel"
                    inputMode="tel"
                    value={signupDetails.phone}
                    onChange={(e) => {
                      const cleaned = e.target.value.replace(/[^\d\s\-\(\)]/g, '');
                      const digitsOnly = cleaned.replace(/\D/g, '');
                      if (selectedPhoneOption?.maxDigits && digitsOnly.length > selectedPhoneOption.maxDigits) {
                        return;
                      }
                      setSignupDetails((prev) => ({ ...prev, phone: cleaned }));
                    }}
                    placeholder={phonePlaceholder}
                    className="w-full h-12 pl-12 pr-4 rounded-xl border border-gray-200 bg-gray-50 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all duration-200"
                    disabled={isLoading !== null}
                  />
                </div>
              </div>

              {/* University */}
              <div className="relative">
                <div className="relative group">
                  <GraduationCap className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 transition-colors group-focus-within:text-blue-500" />
                  <input
                    type="text"
                    value={signupDetails.university}
                    onChange={(e) => setSignupDetails((prev) => ({ ...prev, university: e.target.value }))}
                    onFocus={() => setShowUniversitySuggestions(true)}
                    onBlur={() => setTimeout(() => setShowUniversitySuggestions(false), 150)}
                    placeholder="School / University *"
                    className="w-full h-12 pl-12 pr-4 rounded-xl border border-gray-200 bg-gray-50 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all duration-200"
                    disabled={isLoading !== null}
                  />
                </div>
                {showUniversitySuggestions && (universitySuggestions.length > 0 || isSearchingUniversities) && (
                  <div className="absolute z-10 mt-1 w-full rounded-xl border border-gray-200 bg-white shadow-lg max-h-48 overflow-auto">
                    <ul className="py-1">
                      {isSearchingUniversities && (
                        <li className="px-4 py-2 text-xs text-gray-500 flex items-center gap-2">
                          <Loader2 className="h-3 w-3 animate-spin" />
                          Loading suggestions...
                        </li>
                      )}
                      {universitySuggestions.map((name) => (
                        <li key={name}>
                          <button
                            type="button"
                            onClick={() => {
                              setSignupDetails((prev) => ({ ...prev, university: name }));
                              setShowUniversitySuggestions(false);
                            }}
                            className="w-full text-left px-4 py-2 text-sm hover:bg-blue-50 transition-colors"
                          >
                            {name}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Specialty */}
              <Select
                value={signupDetails.specialty}
                onValueChange={(value) => setSignupDetails((prev) => ({ ...prev, specialty: value }))}
                disabled={isLoading !== null}
              >
                <SelectTrigger className="w-full h-12 px-4 rounded-xl border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all duration-200">
                  <SelectValue placeholder="Specialty (optional)" />
                </SelectTrigger>
                <SelectContent className="max-h-60 overflow-y-auto">
                  {specialties.map((spec) => (
                    <SelectItem key={spec.id} value={spec.name}>
                      {spec.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Program of Study */}
              <Select
                value={signupDetails.programOfStudy}
                onValueChange={(value) => setSignupDetails((prev) => ({ ...prev, programOfStudy: value }))}
                disabled={isLoading !== null}
              >
                <SelectTrigger className="w-full h-12 px-4 rounded-xl border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all duration-200">
                  <SelectValue placeholder="Program of Study *" />
                </SelectTrigger>
                <SelectContent className="max-h-60 overflow-y-auto">
                {NP_PROGRAM_TYPES.map((program) => (
                    <SelectItem key={program.value} value={program.value}>
                      {program.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Zip Code */}
              <div className="relative group">
                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 transition-colors group-focus-within:text-blue-500" />
                <input
                  type="text"
                  value={signupDetails.zipcode}
                  onChange={(e) => setSignupDetails((prev) => ({ ...prev, zipcode: e.target.value }))}
                  placeholder="Zip Code *"
                  className="w-full h-12 pl-12 pr-4 rounded-xl border border-gray-200 bg-gray-50 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all duration-200"
                  disabled={isLoading !== null}
                />
              </div>

              {/* Password */}
              <div className="relative group">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 transition-colors group-focus-within:text-blue-500" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => { if (e.key === ' ') e.preventDefault(); }}
                  placeholder="Password *"
                  className="w-full h-12 pl-12 pr-12 rounded-xl border border-gray-200 bg-gray-50 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all duration-200"
                  disabled={isLoading !== null}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>

              {/* Confirm Password */}
              <div className="relative group">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 transition-colors group-focus-within:text-blue-500" />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onKeyDown={(e) => { if (e.key === ' ') e.preventDefault(); }}
                  placeholder="Confirm Password *"
                  className="w-full h-12 pl-12 pr-12 rounded-xl border border-gray-200 bg-gray-50 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all duration-200"
                  disabled={isLoading !== null}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>

              <button
                type="submit"
                disabled={isLoading !== null || !canSubmit}
                className="w-full h-12 rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 text-white text-sm font-semibold flex items-center justify-center gap-2 hover:from-blue-500 hover:to-blue-600 hover:shadow-lg hover:shadow-blue-500/25 active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none"
              >
                {isLoading === 'email' ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Creating account...
                  </>
                ) : (
                  'Create account'
                )}
              </button>
            </form>

            {/* Sign in link */}
            <div className="mt-6 text-center text-sm text-gray-600">
              Already have an account?{' '}
              <Link 
                href={`/student/${track}/login`} 
                className="text-blue-600 font-semibold hover:text-blue-700 transition-colors"
              >
                Sign in
              </Link>
            </div>
          </div>

          {/* Footer */}
          <p className="mt-8 text-center text-xs text-gray-400">
            © 2024 American Clinical Experience
          </p>
        </div>
      </div>
    </div>
  );
}

export default StudentTrackSignupForm;
